"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Languages, X } from "lucide-react";
import { isLocale, type Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/client";
import { getMessages } from "@/lib/i18n/messages";
import { switchLocalePath } from "@/lib/i18n/paths";
import { hasLocalePreference, saveLocalePreference } from "@/lib/i18n/preference";
import { useConsentDecided } from "@/lib/tracking/consent";
import messages from "./language-suggestion.i18n";
import { track } from "@/lib/tracking/track";

// Si el navegador está en otro idioma soportado y la persona nunca eligió
// uno, se le sugiere UNA vez (sin redirigir a la fuerza). Aceptar o
// descartar guarda la elección, así no vuelve a aparecer.
function browserLocale(): Locale | null {
  try {
    const code = navigator.language?.slice(0, 2).toLowerCase();
    return isLocale(code) ? code : null;
  } catch {
    return null;
  }
}

export function LanguageSuggestion() {
  const lang = useLocale();
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const [suggested, setSuggested] = useState<Locale | null>(null);
  // Espera a que se responda el aviso de cookies (no apilar avisos abajo).
  const consentDecided = useConsentDecided();

  useEffect(() => {
    if (hasLocalePreference()) return;
    const browser = browserLocale();
    // El idioma del navegador se conoce recién en el cliente.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (browser && browser !== lang) setSuggested(browser);
  }, [lang]);

  if (!suggested || !consentDecided) return null;
  const t = getMessages(messages, suggested);

  function accept() {
    if (!suggested) return;
    saveLocalePreference(suggested);
    track("language_change", { language: suggested });
    setSuggested(null);
    router.push(switchLocalePath(pathname, suggested));
  }

  function dismiss() {
    saveLocalePreference(lang);
    setSuggested(null);
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-[1200] mx-auto flex max-w-md items-center gap-3 rounded-xl border border-border bg-card p-3 shadow-lift sm:inset-x-auto sm:right-4"
    >
      <Languages className="h-5 w-5 shrink-0 text-primary" aria-hidden />
      <p className="flex-1 text-sm">{t.question}</p>
      <button
        type="button"
        onClick={accept}
        className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:cursor-pointer hover:bg-primary/90"
      >
        {t.accept}
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t.dismiss}
        className="shrink-0 rounded-full p-1 text-muted-foreground hover:cursor-pointer hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
